import {useMemo, useState} from 'react';
import {flushSync} from 'react-dom';
import {useWatch} from 'react-hook-form';
import {PathUtility} from 'src/shared/constants/PathBuilder';

import {Alert} from 'src/design/atoms/Alert';
import {Center} from 'src/design/atoms/Center';
import {Group} from 'src/design/atoms/Group';
import {Loader} from 'src/design/atoms/Loader';
import {Stack} from 'src/design/atoms/Stack';
import type {JobHazardAnalysisRequest} from 'src/api/ProactApiClient';
import {
  FeatureFlagEnum,
  JobHazardAnalysisStateEnum,
  RiskAssessmentCodeAreaEnum,
} from 'src/api/ProactApiClient';
import {useCreateJobHazardAnalysisMutation} from 'src/redux/queries/jobHazardAnalysis';
import {BackBar} from 'src/app/layout/BackBar';
import {ContentWrapper} from 'src/app/layout/ContentWrapper';
import {PageHeader} from 'src/design/organisms/PageHeader';
import type {PageNavigationConfig} from 'src/app/layout/pageNavigationConfig';
import {buildPageNavigation} from 'src/app/layout/pageNavigationConfig';
import {SidebarLinkJobHazardAnalysis} from 'src/app/layout/Sidebar/SidebarLinksConfig';
import type {JobHazardAnalysisFormValues} from 'src/app/job-hazard-analysis/JobHazardAnalysisForm';
import {
  JobHazardAnalysisForm,
  useJobHazardAnalysisForm,
} from 'src/app/job-hazard-analysis/JobHazardAnalysisForm';
import {
  JobHazardAnalysisPillConfig,
  jhaDocumentTitle,
  pageTitleDict,
} from 'src/app/job-hazard-analysis/jobHazardAnalysisHelpers';
import {routeRegistry} from 'src/pages/RouteRegistry';
import {useGetRiskAssessmentCodeByAreaQuery} from 'src/redux/queries/riskAsessmentCode';
import {useJHAPermissions} from 'src/shared/auth/permissions/useJHAPermissions';
import {useAlert} from 'src/shared/hooks/useAlert';
import useSetDocumentTitle from 'src/shared/hooks/useSetDocumentTitle';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {useBackNavigation} from 'src/shared/hooks/useBackNavigation';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

const navigationConfig: PageNavigationConfig = {
  entityName: pageTitleDict.singular,
  entityNamePlural: pageTitleDict.plural,
  listPath: PathUtility.JobHazardAnalysis(),
  viewPath: id => PathUtility.JobHazardAnalysisView(String(id), 'content'),
};

export const CreateJobHazardAnalysisPage: React.FC = () => {
  useSetDocumentTitle(jhaDocumentTitle);
  const {setAlert} = useAlert();
  const navigate = useAppNavigate();
  const jhaPerms = useJHAPermissions();
  const [isSavePending, setIsSavePending] = useState(false);
  const [createJobHazardAnalysis] = useCreateJobHazardAnalysisMutation();

  const form = useJobHazardAnalysisForm();
  const {control, handleSubmit: handleFormSubmit, reset, getValues} = form;
  const subjectName = useWatch({control, name: 'subjectName'});

  const {
    data: riskAsset,
    isLoading: racIsLoading,
    isError: racIsError,
    error: racError,
  } = useGetRiskAssessmentCodeByAreaQuery(RiskAssessmentCodeAreaEnum.Safety);

  const handleSave = async (values: JobHazardAnalysisFormValues) => {
    try {
      if (!riskAsset) {
        return;
      }

      setIsSavePending(true);

      const body: JobHazardAnalysisRequest = {
        location: values.location,
        riskAssessmentCodeId: riskAsset.id,
        workStartDate: values.workStartDate,
        workFinishDate: values.workFinishDate,
        subjectName: values.subjectName,
        racBefore: 0,
        racAfter: 0,
        racWeighted: 0,
      };

      const resp = await createJobHazardAnalysis(body).unwrap();

      flushSync(() => {
        reset(getValues());
      });
      setAlert(
        `${
          resp.subjectName ?? pageTitleDict.singular
        } was successfully created`,
        'success'
      );

      navigate(PathUtility.JobHazardAnalysisView(String(resp.id), 'content'), {
        replace: true,
      });
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
    setIsSavePending(false);
  };

  const handleValidateAndSave = handleFormSubmit(handleSave);

  const {goBack: handleCancel} = useBackNavigation(
    PathUtility.JobHazardAnalysis()
  );

  const navigation = useMemo(
    () =>
      buildPageNavigation(navigationConfig, {
        mode: 'create',
      }),
    []
  );

  const pills = JobHazardAnalysisPillConfig(JobHazardAnalysisStateEnum.Draft);

  if (racIsError) {
    return (
      <ContentWrapper featureFlag={FeatureFlagEnum.JobHazardAnalysis}>
        <Stack gap={0}>
          <BackBar
            fallbackTo={navigation.fallbackTo}
            fallbackLabel={navigation.fallbackLabel}
            breadcrumbs={navigation.breadcrumbs}
          />
          <Alert color="red" mt="sm" mx="md">
            {parseErrorMessage(racError)}
          </Alert>
        </Stack>
      </ContentWrapper>
    );
  }

  if (!jhaPerms.create) {
    return (
      <ContentWrapper featureFlag={FeatureFlagEnum.JobHazardAnalysis}>
        <Stack gap={0}>
          <BackBar
            fallbackTo={navigation.fallbackTo}
            fallbackLabel={navigation.fallbackLabel}
            breadcrumbs={navigation.breadcrumbs}
          />
          <Alert color="red" mt="sm" mx="md">
            You do not have permission to create Job Hazard Analyses.
          </Alert>
        </Stack>
      </ContentWrapper>
    );
  }

  return (
    <ContentWrapper featureFlag={FeatureFlagEnum.JobHazardAnalysis}>
      <BackBar
        fallbackTo={navigation.fallbackTo}
        fallbackLabel={navigation.fallbackLabel}
        breadcrumbs={navigation.breadcrumbs}
      />

      {racIsLoading ? (
        <Center py="xl">
          <Loader size="lg" />
        </Center>
      ) : (
        <>
          <PageHeader>
            <PageHeader.Title icon={SidebarLinkJobHazardAnalysis.icon}>
              {!subjectName ? `New ${pageTitleDict.singular}` : subjectName}
            </PageHeader.Title>
            {pills.length > 0 && (
              <PageHeader.Badges>
                {pills.map(pill => (
                  <PageHeader.Badge
                    key={pill.text}
                    color={pill.color}
                    variant="light">
                    {pill.text}
                  </PageHeader.Badge>
                ))}
              </PageHeader.Badges>
            )}
            <PageHeader.Actions>
              <PageHeader.Action variant="cancel" onClick={handleCancel}>
                Cancel
              </PageHeader.Action>
              <PageHeader.Action
                variant="create"
                loading={isSavePending}
                onClick={handleValidateAndSave}>
                Create JHA
              </PageHeader.Action>
            </PageHeader.Actions>
          </PageHeader>

          <JobHazardAnalysisForm form={form} mode="create" />

          <Group mt="md">
            <PageHeader.Action variant="cancel" onClick={handleCancel}>
              Cancel
            </PageHeader.Action>
            <PageHeader.Action
              variant="create"
              loading={isSavePending}
              onClick={handleValidateAndSave}>
              Create JHA
            </PageHeader.Action>
          </Group>
        </>
      )}
    </ContentWrapper>
  );
};

// Register the route with the new system
routeRegistry.registerPageRoute({
  backNavigation: 'form',
  path: PathUtility.JobHazardAnalysisCreate(),
  element: <CreateJobHazardAnalysisPage />,
  isPrivate: true,
});
